/* eslint-disable react/require-default-props */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { ActionBar } from '../../styles/layout/ActionBar';

const Controls = styled.div`
   display: flex;
   gap: 8px;
   flex-wrap: wrap;

   && button.page-btn {
      padding: 8px 10px;
      border-radius: 8px;
      background: ${({ theme }) => theme?.colors?.primarySoft ?? '#96c4f0 '};
      cursor: pointer;
      font-weight: 700;
   }

   && button.page-btn.active,
   && button.page-btn:hover {
      background: ${({ theme }) => theme?.colors?.primaryColor ?? '#4098ef'};
      color: #fff;
   }

   && button.page-btn:disabled {
      background: ${({ theme }) =>
         theme?.colors?.primaryDarkColor ?? '#bfbfbf'};
      cursor: not-allowed;
      opacity: 0.5;
   }
`;
// Nota à direita dos controles ("Página 1 de 2 · 25 por página"). Slot
// opcional aberto em 2026-09-12 para a listagem de movimentos; sem `note`,
// nada muda para os demais consumidores.
const Note = styled.span`
   font-size: 12px;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   align-self: center;
`;

export default function PaginationBar({
   currentPage = 1,
   totalPages = 1,
   onPageChange = () => {},
   maxPageNumbersToShow = 5,
   disabled = false,
   note = null,
}) {
   const pages = useMemo(() => {
      const tp = Number(totalPages) || 0;
      const cp = Number(currentPage) || 1;
      const max = Number(maxPageNumbersToShow) || 5;

      if (tp <= 1) return [1];
      if (tp <= max) {
         // mostra tudo, sem reticências
         return Array.from({ length: tp }, (_, i) => i + 1);
      }

      // max aqui é o "tamanho desejado" da janela de números (sem contar reticências)
      // vamos garantir pelo menos 5 (1 ... x ... last)
      const safeMax = Math.max(5, max);

      // quantos vizinhos ao redor da página atual
      const siblingCount = Math.max(1, Math.floor((safeMax - 3) / 2));

      const first = 1;
      const last = tp;

      let left = Math.max(cp - siblingCount, 2);
      let right = Math.min(cp + siblingCount, tp - 1);

      // Ajuste quando está muito perto do começo
      if (cp <= 1 + siblingCount + 1) {
         left = 2;
         right = Math.min(tp - 1, 2 + (safeMax - 3));
      }

      // Ajuste quando está muito perto do fim
      if (cp >= tp - (siblingCount + 1)) {
         right = tp - 1;
         left = Math.max(2, tp - 1 - (safeMax - 3));
      }

      const showLeftDots = left > 2;
      const showRightDots = right < tp - 1;

      const range = [];

      range.push(first);

      if (showLeftDots) range.push('dots-left');

      for (let p = left; p <= right; p += 1) range.push(p);

      if (showRightDots) range.push('dots-right');

      range.push(last);

      return range;
   }, [currentPage, totalPages, maxPageNumbersToShow]);

   const canPrev = currentPage > 1 && totalPages > 0 && !disabled;
   const canNext = totalPages > 0 && currentPage < totalPages && !disabled;

   return (
      <ActionBar
         style={{
            justifyContent: 'space-between',
            width: '100%',
            marginTop: '8px',
         }}
      >
         <Controls>
            <button
               type="button"
               className="page-btn"
               onClick={() => onPageChange(currentPage - 1)}
               disabled={!canPrev}
            >
               Anterior
            </button>

            {pages.map((p) => {
               if (p === 'dots-left' || p === 'dots-right') {
                  return (
                     <span
                        key={p}
                        type=""
                        className="page-btn"
                        disabled
                        aria-hidden="true"
                        style={{
                           display: 'inline-flex',
                           cursor: 'default',
                           flexDirection: 'column',
                           justifyContent: 'flex-end',
                        }}
                     >
                        ...
                     </span>
                  );
               }

               return (
                  <button
                     key={String(p)}
                     type="button"
                     className={`page-btn ${p === currentPage ? 'active' : ''}`}
                     onClick={() => onPageChange(p)}
                     disabled={disabled}
                  >
                     {p}
                  </button>
               );
            })}

            <button
               type="button"
               className="page-btn"
               onClick={() => onPageChange(currentPage + 1)}
               disabled={!canNext}
            >
               Próximo
            </button>
         </Controls>

         {note ? <Note>{note}</Note> : null}
      </ActionBar>
   );
}

PaginationBar.propTypes = {
   currentPage: PropTypes.number,
   totalPages: PropTypes.number,
   onPageChange: PropTypes.func,
   maxPageNumbersToShow: PropTypes.number,
   disabled: PropTypes.bool,
   note: PropTypes.node,
};
